// Unpublished Work © 2021-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import DataGridWrapper from 'Ui/components/common/data-table/data-grid-wrapper';
import EditLink from 'Ui/components/common/data-table/edit-link';
import EquipmentName from 'OnEquip/equipment/common/data-table/equipment-name';
import Duration from 'OnEquip/equipment/service-history/data-table/duration';
import {deleteService} from 'Services/maintenance-service';
import {getMaintenanceDue} from 'Services/membership-service';
import {fetchEffectData} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {formatTimeOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {
    formatAssignees,
    formatModelAndManufacturerName
} from 'Ui/models/maintenance';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import dialogTypes from 'Ui/components/common/dialog-types';
import {useEquipmentContext} from 'OnEquip/equipment/context/equipment-context';
import {HISTORY} from 'OnEquip/equipment/constants/equipment-details-tabs';
import permissionsConstants from 'Common/constants/permissions';
import {SERVICE_HISTORY} from 'Common/constants/routes';
import {COMPLETED, DECLINED} from 'Common/constants/service-status';
import accounting from 'accounting';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {VIEW_SERVICE, MANAGE_SERVICE, VIEW_EQUIPMENT_DETAILS} from 'Common/constants/business-activities';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {IconWdt} from '@deere/icons';
import {exportServiceHistoryReport} from 'Services/excel-service';
import {MAINTENANCE_MANAGER_PROGOLF, PRO_GOLF_LABOR_REPORTS_1} from 'Common/constants/isg-toggles';
import LaborReportExportDialog from 'OnLabor/report/labor-report-export-dialog';
import {getServiceHistoryExportContent} from 'Utils/labor-report-export-utils';
import useReportExportDialog from 'Ui/react-hooks/use-report-export-dialog';

function getColumns(translations, membershipId, history) {
    const onequipUrl = `${window.props.onLinkLegacyUri}/onequip/#m/${membershipId}/fleet/{0}/serviceHistory`;
    const equipmentUrl = `${onequipUrl}/equipment/{1}`;
    const serviceUrl = `${SERVICE_HISTORY}/serviceForm?serviceId={0}`;

    return [
        {
            headerName: translations.DATE,
            field: 'unFormattedTime',
            minWidth: 155,
            flex: 1,
            renderCell: (params) => params.row.formattedTime
        },
        {
            headerName: translations.ONLINK_ASSIGNEES,
            minWidth: 155,
            field: 'assignees',
            flex: 1
        },
        {
            headerName: translations.EQUIPMENT_LIST,
            field: 'equipmentName',
            minWidth: 120,
            flex: 1,
            renderCell: (params) => (
                <EquipmentName
                    equipment={params.row}
                    requiredMyJdPermissions={[VIEW_EQUIPMENT_DETAILS]}
                    tab={HISTORY}
                    type={SERVICE_HISTORY}
                    url={equipmentUrl}
                />
            )
        },
        {
            headerName: translations.MODEL,
            minWidth: 120,
            field: 'modelAndManufacturerName',
            flex: 1
        },
        {
            headerName: translations.ONLINK_GROUP,
            minWidth: 120,
            field: 'serviceGroup',
            flex: 1
        },
        {
            headerName: translations.ONLINK_SERVICE,
            minWidth: 120,
            field: 'serviceTypeName',
            flex: 1,
            renderCell: (params) => {
                const filledInServiceUrl = serviceUrl.replace('{0}', params.row.serviceId);

                return (
                    <EditLink
                        onClick={() => history.push(filledInServiceUrl)}
                        requiredMyJdPermissions={[VIEW_SERVICE]}
                    >
                        {params.row.serviceTypeName}
                    </EditLink>
                );
            }
        },
        {
            headerName: replaceTranslationNames(translations.DURATION_WITH_UNIT, {
                '0': translations.mins
            }),
            field: 'actualDuration',
            type: 'number',
            minWidth: 150,
            renderCell: (params) => (
                <Duration
                    service={params.row}
                    translations={translations}
                />
            )
        },
        {
            headerName: translations.HOURS,
            field: 'totalHours',
            type: 'number',
            minWidth: 70,
            flex: 1,
            valueFormatter: (value) => accounting.formatNumber(value, 1)
        }
    ];
}

function initializeState() {
    const [services, setServices] = React.useState(() => []);
    const [loading, setLoading] = React.useState(() => false);

    return {
        services,
        setServices,
        loading,
        setLoading
    };
}

function ServiceHistory(props) {
    const {
        closeConfirmation,
        history,
        isgToggles,
        isMigrated,
        membershipId,
        myJdPermissions,
        openConfirmation,
        permissions,
        superUser,
        translations
    } = props;

    const {
        services,
        setServices,
        loading,
        setLoading
    } = initializeState();

    const {addToast} = useToast();
    const equipmentContext = useEquipmentContext();
    const isFleetToMaintenanceEnabled = isgToggles[MAINTENANCE_MANAGER_PROGOLF];
    const isExportPreviewEnabled = Boolean(isgToggles?.[PRO_GOLF_LABOR_REPORTS_1]);
    const exportContent = React.useMemo(() => getServiceHistoryExportContent(services, translations), [services, translations]);
    const downloadReport = React.useCallback(() => exportServiceHistoryReport(services, translations, addToast), [services, translations, addToast]);
    const {
        handleClose,
        handleDownload,
        handleOpen,
        isOpen
    } = useReportExportDialog(downloadReport, isExportPreviewEnabled);

    const canDeleteServices = React.useMemo(() => {
        return isAuthorized({
            myJdPermissions: [MANAGE_SERVICE],
            permission: permissionsConstants.DELETE_SERVICE_TICKETS
        }, {
            isMigrated,
            myJdPermissions,
            permissions,
            superUser
        }) && !isFleetToMaintenanceEnabled;
    }, [isMigrated, myJdPermissions, permissions, superUser, isFleetToMaintenanceEnabled]);

    equipmentContext.current = async (isMounted = () => true) => {
        setLoading(true);

        const services = await getMaintenanceDue(`${COMPLETED},${DECLINED}`);

        if (isMounted()) {
            const formattedServices = services.map((service) => ({
                ...service,
                assignees: formatAssignees(service),
                formattedTime: formatTimeOrDefault(service.timeCompleted) || formatTimeOrDefault(service.dateScheduled),
                unFormattedTime: service.timeCompleted || service.dateScheduled,
                modelAndManufacturerName: formatModelAndManufacturerName(service)
            }));

            setServices(formattedServices);
            setLoading(false);
        }
    };

    React.useEffect(() => fetchEffectData(equipmentContext.current), [membershipId]);

    async function deleteServiceHistory(data) {
        const serviceId = data.serviceId;
        const serviceName = data.serviceTypeName;

        openConfirmation({
            message: translations.ONLINK_SERVICE_DELETE_CONFIRMATION_MESSAGE,
            title: translations.ONLINK_DELETE_SERVICE,
            async onContinue() {
                closeConfirmation();

                try {
                    await deleteService(serviceId);

                    await equipmentContext.current();
                } catch {
                    const message = replaceTranslationNames(translations.ONLINK_SERVICE_DELETE_ERROR, {
                        '0': serviceName
                    });

                    addToast({
                        message,
                        type: TOAST_TYPE.ERROR
                    });
                }
            },
            onCancel: closeConfirmation
        });
    }

    useNavBarActions([{
        disabled: loading,
        Icon: <IconWdt/>,
        onClick: handleOpen,
        myJdPermissions: VIEW_EQUIPMENT_DETAILS,
        title: 'EXPORT',
        variant: 'primary'
    }]);

    return (
        <>
            <DataGridWrapper
                columns={getColumns(translations, membershipId, history)}
                deleteHandler={deleteServiceHistory}
                getRowId={(row) => row.serviceId}
                initialState={{
                    sorting: {
                        sortModel: [{
                            field: 'unFormattedTime',
                            sort: 'desc'
                        }]
                    }
                }}
                loading={loading}
                pinnedColumns={{
                    left: [],
                    right: []
                }}
                rows={services}
                showDeleteColumn={canDeleteServices}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
            {
                isOpen &&
                <LaborReportExportDialog
                    dateRange={translations.ALL_TIME}
                    onCancel={handleClose}
                    onDownload={handleDownload}
                    rows={[
                        exportContent.header,
                        ...exportContent.rows
                    ]}
                    translations={translations}
                />
            }
        </>
    );
}

ServiceHistory.propTypes = {
    closeConfirmation: PropTypes.func,
    history: PropTypes.history,
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    membershipId: PropTypes.string,
    myJdPermissions: PropTypes.myJdPermissions,
    openConfirmation: PropTypes.func,
    permissions: PropTypes.legacyPermissions,
    superUser: PropTypes.bool,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isMigrated: state.membership.isMigrated,
        isgToggles: state.account.isgToggles,
        membershipId: state.membership.membershipId,
        myJdPermissions: state.account.myJdPermissions,
        permissions: state.account.permissions,
        superUser: state.account.superUser
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ServiceHistory));
